import { get, set, size, camelCase } from 'global/lib/lodash';
import { getCategoryByComponent, DEFAULT_FIELDS } from '../../admin/task-types/constants';
import { SETTINGS, APP_MODE_IDS } from '../../admin/account-settings';
import { getAccountSetting } from 'redux/actions/userLogin';
import { parseJson } from 'global/utils';
import sendData from 'redux/actions/sendData';

/**
 * Convert a task-type custom field definition into a Form input descriptor.
 */
export const customFieldToFormInput = (field, values = {}, opts = {}) => {
  const { disabled = false, errors = [] } = opts;
  const fieldId = `custom_field_values.${field.id}`;
  const currentValue = get(values, field.id, get(values, `custom_field_values.${field.id}`));
  const hasError = errors.some((e) => e.id === field.id);
  const catEntry = getCategoryByComponent(field.field_type);
  const catKey = catEntry ? catEntry[0] : 'Text';

  const base = {
    id: fieldId,
    required: field.is_required,
    props: {
      label: field.label,
      disabled,
      ...(hasError ? { error: true, helperText: 'This field is required' } : {}),
    },
  };

  const subType = get(field, 'config.sub_type', field.field_type);

  switch (catKey) {
    case 'Text': {
      const type = subType === 'RichText' ? 'RichText' : 'TextField';
      const isLongText = subType === 'LongText';
      return {
        ...base,
        type,
        defaultValue: () => currentValue || get(field, 'config.default_text', ''),
        props: {
          ...base.props,
          placeholder: get(field, 'config.prompt_text', ''),
          ...(type === 'TextField' && isLongText ? { multiline: true, minRows: 3 } : {}),
        },
      };
    }

    case 'Select': {
      const options = get(field, 'config.options') || [];
      const formattedOptions = options.map((o) => {
        if (typeof o === 'string') return { label: o, value: o };
        const label = o.label || o.title || o.value || '';
        const value = o.value || o.label || o.title || '';
        return { label, value };
      });

      // Resolve the actual form component type from sub_type
      const formType = subType || 'Select';

      if (formType === 'RadioButton') {
        return {
          ...base,
          type: 'RadioButton',
          options: formattedOptions.map((o) => ({ label: o.label, title: o.label, value: o.value })),
          defaultValue: () => currentValue || '',
        };
      }

      if (formType === 'Checkbox') {
        return {
          ...base,
          type: 'Checkbox',
          options: formattedOptions.map((o) => ({ label: o.label, value: o.value })),
          defaultValue: () => currentValue || [],
        };
      }

      if (formType === 'MultiSelect') {
        return {
          ...base,
          type: 'MultiSelect',
          data: formattedOptions.map((o) => ({ title: o.label, value: o.value })),
          defaultValue: () => currentValue || [],
        };
      }

      if (formType === 'Autocomplete') {
        const isMultiple = get(field, 'config.allow_multiple', false);
        return {
          ...base,
          type: 'Autocomplete',
          data: formattedOptions.map((o) => ({ id: o.value, name: o.label, label: o.label, value: o.value })),
          defaultValue: () => currentValue || (isMultiple ? [] : ''),
          props: {
            ...base.props,
            multiple: isMultiple,
          },
        };
      }

      if (formType === 'ChipAdd') {
        return {
          ...base,
          type: 'ChipSelect',
          data: formattedOptions.map((o) => ({ label: o.label, value: o.value })),
          allowCustomInput: get(field, 'config.allow_custom_input', false),
          allowMultiple: get(field, 'config.allow_multiple', false),
          defaultValue: () => currentValue || [],
        };
      }

      // Default: Select dropdown
      return {
        ...base,
        type: 'Select',
        data: formattedOptions.map((o) => ({ title: o.label, value: o.value })),
        defaultValue: () => currentValue || '',
      };
    }

    case 'Toggle': {
      const options = get(field, 'config.options') || [];
      return {
        ...base,
        type: 'ToggleButton',
        options: options.map((o) =>
          typeof o === 'string' ? { title: o, value: o } : { title: o.title || o.label || o.value || '', value: o.value || '' }
        ),
        defaultValue: () => currentValue || '',
        props: { ...base.props, exclusive: true },
      };
    }

    case 'Date': {
      return {
        ...base,
        type: 'KeyboardDatePicker',
        defaultValue: () => currentValue || (get(field, 'config.default_date') === 'today' ? new Date() : undefined),
        props: {
          ...base.props,
          disablePast: !get(field, 'config.allow_previous_dates', true),
        },
      };
    }

    case 'Media': {
      const isDocUpload = subType === 'DropZone';
      const isBothUpload = subType === 'Both';
      const taskImages = get(opts, 'taskImages', []);
      const mediaHelperText = get(field, 'config.helper_text', '');
      return {
        ...base,
        type: 'DropArea',
        defaultValue: () => {
          if (!currentValue || !currentValue.length) return [];
          // Hydrate {type, id} references from task images when available
          if (taskImages.length && currentValue[0].type === 'media' && !currentValue[0].fullUrl && !currentValue[0].file) {
            return currentValue
              .map(ref => {
                const found = taskImages.find(ti => String(ti.id) === String(ref.id));
                return found ? { ...found, type: 'media' } : null;
              })
              .filter(Boolean);
          }
          return currentValue;
        },
        props: {
          ...base.props,
          ...(mediaHelperText ? { helperText: mediaHelperText } : {}),
          maxFiles: get(field, 'config.max_allowed', 10),
          collection: isBothUpload ? 'task-images' : (isDocUpload ? 'task-files' : 'task-images'),
          uploadAllNew: true,
          shouldUpdateOnCaptionChange: !isDocUpload,
          spaceBetween: isDocUpload ? 0 : 5,
          ...(isBothUpload ? { bothMode: true, accept: null } : {}),
          ...(isDocUpload ? { accept: null, docMode: true } : {}),
          ...(!isBothUpload && size(get(field, 'config.file_types_allowed')) && !isDocUpload ? { accept: field.config.file_types_allowed } : {}),
        },
      };
    }

    case 'Repeater': {
      const nestedFields = get(field, 'config.nested_fields', []);
      const itemName = get(field, 'config.item_name', 'Item');
      const helperText = get(field, 'config.helper_text', '');
      // Build standard Form input descriptors for each nested field
      const nestedInputs = nestedFields.map((nf) => {
        const nfConfig = nf.config || {};
        const nfSubType = get(nfConfig, 'sub_type', nf.type);
        const id = nf.label || nf.type;
        const nfHelperText = get(nfConfig, 'helper_text', '');
        const placeholder = get(nfConfig, 'prompt_text', '');

        if (nf.type === 'Select' || nf.type === 'Toggle') {
          const options = get(nfConfig, 'options', []).map((o) =>
            typeof o === 'string' ? { title: o, value: o } : { title: o.label || o.title, value: o.value }
          );
          return {
            id,
            type: 'Select',
            data: options,
            props: { label: id, ...(nfHelperText ? { helperText: nfHelperText } : {}) },
          };
        }
        if (nf.type === 'Date') {
          return {
            id,
            type: 'KeyboardDatePicker',
            props: { label: id, ...(nfHelperText ? { helperText: nfHelperText } : {}) },
          };
        }
        if (nfSubType === 'RichText') {
          return {
            id,
            type: 'RichText',
            props: { label: id, ...(nfHelperText ? { helperText: nfHelperText } : {}) },
          };
        }
        if (nf.type === 'Media') {
          const nfIsDoc = nfSubType === 'DropZone';
          const nfIsBoth = nfSubType === 'Both';
          return {
            id,
            type: 'DropArea',
            defaultValue: () => [],
            props: {
              label: id,
              maxFiles: get(nfConfig, 'max_allowed', 10),
              collection: nfIsBoth ? 'task-images' : (nfIsDoc ? 'task-files' : 'task-images'),
              uploadAllNew: true,
              shouldUpdateOnCaptionChange: !nfIsDoc,
              ...(nfHelperText ? { helperText: nfHelperText } : {}),
              ...(nfIsBoth ? { bothMode: true, accept: null } : {}),
              ...(nfIsDoc ? { accept: null, docMode: true } : {}),
            },
          };
        }
        // Default: TextField (or LongText multiline variant)
        return {
          id,
          type: 'TextField',
          props: {
            label: id,
            ...(placeholder ? { placeholder } : {}),
            ...(nfHelperText ? { helperText: nfHelperText } : {}),
            ...(nfSubType === 'LongText' ? { multiline: true, minRows: 3 } : {}),
          },
        };
      });
      // The API normalizer (json-api-normalizer) recursively camelCases all
      // keys, so saved items like { "My Field": "val" } become { "myField": "val" }.
      // Build a lookup to map camelCased keys back to the original labels.
      const labelByCamel = {};
      nestedFields.forEach((nf) => {
        const label = nf.label || nf.type;
        labelByCamel[camelCase(label)] = label;
      });
      const taskImgs = get(opts, 'taskImages', []);
      const normalizeItems = (items) =>
        (items || []).map((item) => {
          if (!item || typeof item !== 'object') return item;
          const out = {};
          Object.keys(item).forEach((k) => {
            const origLabel = labelByCamel[k] || labelByCamel[camelCase(k)] || k;
            let val = item[k];
            // Hydrate {type, id} media references from task images/files
            if (Array.isArray(val) && val.length && val[0] && val[0].type === 'media' && val[0].id && !val[0].fullUrl && !val[0].file) {
              val = val.map(ref => {
                const found = taskImgs.find(ti => String(ti.id) === String(ref.id));
                return found ? { ...found, type: 'media' } : ref;
              });
            }
            out[origLabel] = val;
          });
          return out;
        });

      return {
        ...base,
        type: 'Repeater',
        defaultValue: () => normalizeItems(currentValue),
        nestedInputs,
        props: {
          ...base.props,
          itemName,
          ...(helperText ? { helperText } : {}),
        },
      };
    }

    default:
      return {
        ...base,
        type: 'TextField',
        defaultValue: () => currentValue || '',
      };
  }
};

/**
 * Get Form input descriptors for all enabled custom fields of a task type.
 */
export const getCustomFieldInputs = (taskType, values = {}, opts = {}) => {
  if (!taskType || !taskType.custom_fields) return [];

  return taskType.custom_fields
    .filter((f) => f.is_enabled)
    .sort((a, b) => a.order - b.order)
    .map((field) => customFieldToFormInput(field, values, opts));
};

/**
 * Validate that all required custom fields have values.
 * Returns { valid: boolean, missingFields: [] }
 */
export const validateRequiredCustomFields = (taskType, values = {}) => {
  if (!taskType || !taskType.custom_fields) return { valid: true, missingFields: [] };

  const missingFields = taskType.custom_fields
    .filter((f) => f.is_enabled && f.is_required)
    .filter((f) => {
      const val = get(values, f.id, get(values, `custom_field_values.${f.id}`));
      if (val === undefined || val === null || val === '') return true;
      if (Array.isArray(val) && val.length === 0) return true;
      return false;
    });

  return { valid: missingFields.length === 0, missingFields };
};

// Maps standard field IDs to form state keys and value check types
const STANDARD_FIELD_MAP = {
  f_title: { formKey: 'name', label: 'Title', checkEmpty: (v) => !v },
  f_description: { formKey: 'description', label: 'Description', checkEmpty: (v) => !v },
  f_assignee: { formKey: 'assignedTo', label: 'Assignee', checkEmpty: (v) => !size(v) },
  f_due_date: { formKey: 'dueDate', label: 'Due Date', checkEmpty: (v) => !v },
  f_media: { formKey: 'media', label: 'Media', checkEmpty: (v) => !size(v) },
  f_trade: { formKey: 'tradeTypes', label: 'Trade Types', checkEmpty: (v) => !size(v) },
};

/**
 * Get the merged required state for all standard fields on a task type,
 * combining DEFAULT_FIELDS with default_field_overrides.
 */
export const getStandardFieldRequirements = (taskType) => {
  if (!taskType) return DEFAULT_FIELDS;
  const overrides = get(taskType, 'default_field_overrides') || {};
  return DEFAULT_FIELDS.map((df) => {
    const ov = overrides[df.id];
    return ov ? { ...df, is_enabled: ov.is_enabled ?? df.is_enabled, is_required: ov.is_required ?? df.is_required } : df;
  });
};

/**
 * Validate that all required standard fields have values.
 * Returns { valid: boolean, missingFields: [{ id, formKey, label }] }
 */
export const validateRequiredStandardFields = (taskType, formValues = {}) => {
  if (!taskType) return { valid: true, missingFields: [] };
  const reqs = getStandardFieldRequirements(taskType);
  const missingFields = reqs
    .filter((f) => f.is_enabled && f.is_required && STANDARD_FIELD_MAP[f.id])
    .filter((f) => {
      const mapping = STANDARD_FIELD_MAP[f.id];
      const val = get(formValues, mapping.formKey);
      return mapping.checkEmpty(val);
    })
    .map((f) => ({ id: f.id, formKey: STANDARD_FIELD_MAP[f.id].formKey, label: STANDARD_FIELD_MAP[f.id].label }));
  return { valid: missingFields.length === 0, missingFields };
};

/**
 * Convert a task type's closeout_requirements to the format expected by the
 * meta Checkbox input (same shape as taskOptions() returns).
 */
export const getCloseoutFromTaskType = (taskType) => {
  if (!taskType || !taskType.closeout_requirements) return [];

  const options = [];
  if (taskType.closeout_requirements.notes_required !== undefined) {
    options.push({ label: 'Notes required to close', value: 'notes_required' });
  }
  if (taskType.closeout_requirements.photos_required !== undefined) {
    options.push({ label: 'Photos required to close', value: 'photos_required' });
  }
  if (taskType.closeout_requirements.signature_required !== undefined) {
    options.push({ label: 'Signature required to close', value: 'signature_required' });
  }
  return options;
};

/**
 * Get the default meta value from a task type's closeout_requirements.
 * Returns an object like { notes_required: true, photos_required: false }.
 */
export const getCloseoutDefaults = (taskType) => {
  if (!taskType || !taskType.closeout_requirements) return {};
  return { ...taskType.closeout_requirements };
};

/**
 * Filter task types by active status and active modes based on account settings.
 * Inactive task types and types belonging to disabled modes are excluded.
 */
export const getAvailableTaskTypes = (taskTypes = []) => {
  const appModes = getAccountSetting(SETTINGS.appMode);
  const _parsed = Array.isArray(appModes) ? appModes : parseJson(appModes, [APP_MODE_IDS.Quality]);
  const appModeArr = (Array.isArray(_parsed) ? _parsed : [APP_MODE_IDS.Quality]).map(Number);

  return taskTypes.filter((tt) => {
    if (!tt.is_active) return false;
    if (tt.mode === 'Quality' && !appModeArr.includes(APP_MODE_IDS.Quality)) return false;
    if (tt.mode === 'Safety' && !appModeArr.includes(APP_MODE_IDS.Safety)) return false;
    return true;
  });
};

/**
 * Filter available task types by realm tier.
 * Types with no location_types (empty array) are shown everywhere.
 * Types with location_types are only shown if the tier matches.
 */
export const getAvailableTaskTypesForTier = (taskTypes = [], tier) => {
  if (!tier) return taskTypes;
  return taskTypes.filter((tt) => !size(tt.location_types) || tt.location_types.includes(tier));
};

/**
 * Extract the task type ID from a task object.
 * Checks all known paths where the ID may be stored.
 */
export const getTaskTypeIdFromTask = (task) => {
  return get(task, 'taskTypeId', get(task, 'task_type_id', get(task, 'meta.task_type_id', get(task, 'meta.taskTypeId',
    get(task, 'taskType.id')))));
};

/**
 * Find a task type by ID from a list.
 */
export const getTaskTypeById = (taskTypes = [], id) => {
  if (!id) return null;
  return taskTypes.find((tt) => String(tt.id) === String(id)) || null;
};

/**
 * Map of task type key (lowercased, normalized) → Material icon name.
 * Keys support both kebab-case handles and space-separated names.
 */
const TASK_TYPE_ICONS = {
  issue: 'check_box',
  'action-plan': 'assignment',
  'action plan': 'assignment',
  'toolbox-talk': 'check_circle',
  'toolbox talk': 'check_circle',
  hazard: 'warning',
  incident: 'cancel',
};

/**
 * Return the Material icon name for a task type based on its handle or name.
 */
export const getTaskTypeIcon = (taskType) => {
  const handle = (taskType?.handle || '').toLowerCase();
  const name = (taskType?.name || '').toLowerCase();
  return TASK_TYPE_ICONS[handle] || TASK_TYPE_ICONS[name] || 'task';
};

const isMediaArray = (val) =>
  Array.isArray(val) && val.length > 0 && val.some(item => item && typeof item === 'object' && item.id && (item.type === 'media' || item.fullUrl || item.collectionName || item.collection_name));

const isRepeaterArray = (val) =>
  Array.isArray(val) && val.length > 0 && val.every(item => item && typeof item === 'object' && !item.id);

/**
 * Strip full media objects from custom_field_values down to {type, id} for API payloads.
 * Media fields store full objects (with URLs, attributes, etc.) in form state but the
 * backend only expects relationship-style references.
 * Also handles media nested inside repeater field items.
 */
export const cleanCustomFieldMedia = (cfv) => {
  if (!cfv || typeof cfv !== 'object') return cfv;
  const cleaned = { ...cfv };
  for (const key in cleaned) {
    const val = cleaned[key];
    if (isMediaArray(val)) {
      cleaned[key] = val.map(item => ({ type: 'media', id: `${item.id}` }));
    } else if (isRepeaterArray(val)) {
      cleaned[key] = val.map(item => {
        const cleanedItem = { ...item };
        // Strip internal _key used for React keys - not part of the data model
        delete cleanedItem._key;
        for (const nestedKey in cleanedItem) {
          if (isMediaArray(cleanedItem[nestedKey])) {
            cleanedItem[nestedKey] = cleanedItem[nestedKey].map(m => ({ type: 'media', id: `${m.id}` }));
          }
        }
        return cleanedItem;
      });
    }
  }
  return cleaned;
};

/**
 * Extract media IDs from custom_field_values, separated by collection.
 * Must be called with RAW (uncleaned) values so collection_name is available.
 * Returns { imageIds: string[], fileIds: string[] }.
 */
export const getCustomFieldMediaIdsByCollection = (cfv) => {
  if (!cfv || typeof cfv !== 'object') return { imageIds: [], fileIds: [] };
  const imageIds = [];
  const fileIds = [];
  const collectMediaIds = (val) => {
    if (isMediaArray(val)) {
      val.forEach(item => {
        if (!item.id) return;
        const col = item.collection_name || item.collectionName || '';
        if (col === 'task-files') {
          fileIds.push(String(item.id));
        } else {
          imageIds.push(String(item.id));
        }
      });
    }
  };
  for (const key in cfv) {
    const val = cfv[key];
    collectMediaIds(val);
    if (isRepeaterArray(val)) {
      val.forEach(item => {
        for (const nestedKey in item) {
          collectMediaIds(item[nestedKey]);
        }
      });
    }
  }
  return { imageIds, fileIds };
};

/**
 * Extract all media IDs from custom_field_values (flat list).
 * Works with both raw (full objects) and cleaned ({type, id}) values.
 */
export const getCustomFieldMediaIds = (cfv) => {
  const { imageIds, fileIds } = getCustomFieldMediaIdsByCollection(cfv);
  return [...imageIds, ...fileIds];
};

/**
 * Patch captions/order on custom field media items that have been changed.
 * Call this with the raw (uncleaned) custom_field_values before cleanCustomFieldMedia.
 */
const patchMediaCaptionsForArray = async (val) => {
  if (!isMediaArray(val)) return;
  await Promise.all(
    val.filter(item => item.hasChanged && item.id && !`${item.id}`.includes('_file_'))
      .map(item => sendData({
        url: `/media/${item.id}`,
        method: 'patch',
        data: { data: { id: `${item.id}`, type: 'media', attributes: { order_column: item.order_column, custom_properties: { ...(item.custom_properties || {}), caption: item.caption || '' } } } },
      }))
  );
};

export const patchCustomFieldMediaCaptions = async (cfv) => {
  if (!cfv || typeof cfv !== 'object') return;
  for (const key in cfv) {
    const val = cfv[key];
    await patchMediaCaptionsForArray(val);
    if (isRepeaterArray(val)) {
      for (const item of val) {
        for (const nestedKey in item) {
          await patchMediaCaptionsForArray(item[nestedKey]);
        }
      }
    }
  }
};

/**
 * Process custom field media on a task payload (similar to UploadFiles).
 * Handles: caption patching, value cleaning, relationship management.
 *
 * Call after building the data payload (and after UploadFiles if applicable).
 *
 * @param {object} data - JSON:API payload (mutated in place)
 * @param {object} opts
 * @param {object} opts.rawCfv - Custom field values (raw with full media objects for collection detection)
 * @param {string} opts.taskTypeId - Task type ID (skips processing if falsy)
 * @param {boolean} [opts.isClean] - If true, skip cleaning (already done by caller)
 * @param {boolean} [opts.skipCaptions] - If true, skip caption patching (already done by caller)
 */
export const UploadCustomFieldMedia = async (data, opts = {}) => {
  const { rawCfv, taskTypeId, isClean = false, skipCaptions = false } = opts;
  if (!taskTypeId || !rawCfv || typeof rawCfv !== 'object') return;

  // Collect media IDs by collection BEFORE cleaning (raw objects have collection_name)
  const { imageIds, fileIds } = getCustomFieldMediaIdsByCollection(rawCfv);

  let cleanedCfv;
  if (isClean) {
    cleanedCfv = rawCfv;
  } else {
    if (!skipCaptions) await patchCustomFieldMediaCaptions(rawCfv);
    cleanedCfv = cleanCustomFieldMedia(rawCfv);
  }

  // Set cleaned cfv on meta only (not duplicated as a root attribute)
  const meta = get(data, 'data.attributes.meta', {});
  set(data, 'data.attributes.meta', { ...meta, custom_field_values: cleanedCfv });

  // Add image media IDs to images relationship
  if (size(imageIds)) {
    const existingRel = get(data, 'data.relationships.images.data', []);
    const existingIds = new Set(existingRel.map(r => String(r.id)));
    const newRefs = imageIds.filter(id => !existingIds.has(id)).map(id => ({ id, type: 'media' }));
    set(data, 'data.relationships.images.data', [...existingRel, ...newRefs]);
    set(data, 'data.attributes.images', undefined);
  }

  // Add document media IDs to files relationship
  if (size(fileIds)) {
    const existingFiles = get(data, 'data.relationships.files.data', []);
    const existingFileIds = new Set(existingFiles.map(r => String(r.id)));
    const newFileRefs = fileIds.filter(id => !existingFileIds.has(id)).map(id => ({ id, type: 'media' }));
    set(data, 'data.relationships.files.data', [...existingFiles, ...newFileRefs]);
  }
};

/**
 * Check which app modes are currently active.
 */
export const getActiveAppModes = () => {
  const appModes = getAccountSetting(SETTINGS.appMode);
  const _parsed = Array.isArray(appModes) ? appModes : parseJson(appModes, [APP_MODE_IDS.Quality]);
  const appModeArr = (Array.isArray(_parsed) ? _parsed : [APP_MODE_IDS.Quality]).map(Number);
  const quality = appModeArr.includes(APP_MODE_IDS.Quality);
  const safety = appModeArr.includes(APP_MODE_IDS.Safety);
  return { quality, safety, bothActive: quality && safety };
};

/**
 * Group available task types by mode.
 * When only one mode is active, returns { grouped: false, items }.
 * When both modes are active, returns { grouped: true, groups: [{ label, items }] }.
 */
export const groupTaskTypesByMode = (taskTypes) => {
  const quality = taskTypes.filter((tt) => tt.mode === 'Quality');
  const safety = taskTypes.filter((tt) => tt.mode === 'Safety');

  if (!quality.length || !safety.length) return { grouped: false, items: taskTypes };

  return {
    grouped: true,
    groups: [
      { label: 'quality', items: quality },
      { label: 'safety', items: safety },
    ],
  };
};
